// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { pathnameWithoutLocale, searchPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import {
  useEsaExperience,
  useEsaWaitPeriod,
  useFundingProgramAbbr,
  useSelfElectedStateFundingProgram,
  useSession,
} from "@outschool/ui-auth";
import _ from "lodash";
import React, { useCallback, useContext, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { useActiveLearnerSearchFilters } from "../hooks/useActiveLearnerSearchFilters";
import { useSearchByLearner } from "../hooks/useSearchByLearner";

/**
 * Type extended for filters used in the UI but not sent to the backend
 * (userName is always paired with userUid, but allows us to reconstruct
 * the UI)
 */
export type ExtendedSearchFiltersType = SearchFiltersType & {
  userName?: string | null;
};

export type SetFiltersOptions = {
  overrideRouting?: boolean;
};

/**
 * These filter categories attempt to describe the set of ways that filters can be applied to search results.
 * The filterCategory is stored in the history state of the search page and then tracked in Segment events, e.g. searchFiltersApplied.
 */
export const FilterCategory = {
  // Top-level search and query filters
  Query: "Query",
  // For legacy reasons, there are both "Query" and "q" filter categories.
  q: "q",

  SearchByLearner: "SearchByLearner",

  // Common filter categories
  Age: "Age",
  GradeLevel: "GradeLevel",
  Theme: "Theme",
  Schedule: "Schedule",
  Date: "Date",
  EnglishProficiency: "EnglishProficiency",
  Capacity: "Capacity",
  FundingPrograms: "FundingPrograms",
  Delivery: "Delivery",

  // Any of the myriad filters in the uncommon filters dropdown area.
  Uncommon: "Uncommon",

  // Additional filters that are not part of the main filters
  // Used for mobile search.
  additional: "additional",

  // Special filter category for when multiple filters are applied at once
  MultiFilter: "MultiFilter",

  // Preset sets of filters
  preset: "preset",

  // Special "filter categories" for not-quite-filters
  Order: "Order",
  Reset: "Reset",

  // Unknown
  showListings: "showListings",
} as const;
export type FilterCategory =
  (typeof FilterCategory)[keyof typeof FilterCategory];

export type SetFiltersType = (
  category: FilterCategory,
  partialFilters: ExtendedSearchFiltersType,
  options?: SetFiltersOptions,
) => void;

interface SearchFiltersContextValue {
  setFilters: SetFiltersType;
  filters: ExtendedSearchFiltersType;
}

type InternalSearchFiltersContextValue = Partial<SearchFiltersContextValue>;

export const SearchFiltersContext =
  React.createContext<InternalSearchFiltersContextValue>({});

export function SearchFiltersContextProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const { isLoggedIn, currentUser } = useSession();
  const esaWaitPeriod = useEsaWaitPeriod();

  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const getActiveLearnerSearchFilters = useActiveLearnerSearchFilters();
  const { learner } = useSearchByLearner({
    onFilterChange: learner => {
      learnerRef.current = learner;
      if (pathnameWithoutLocale(location.pathname) === searchPath()) {
        setFilters(
          FilterCategory.SearchByLearner,
          getActiveLearnerSearchFilters(learner),
        );
      }
    },
  });
  const learnerRef = React.useRef(learner);

  const esaState = useFundingProgramAbbr();
  const { sessionStateFundingProgram } = useEsaExperience();
  const { selfElectedStateFundingProgram, deactivateSearchFilters } =
    useSelfElectedStateFundingProgram();
  const [persistedFundingPrograms, setPersistedFundingPrograms] =
    React.useState(!!esaState ? [esaState] : undefined);

  useEffect(() => {
    if (!isLoggedIn && sessionStateFundingProgram?.stateAbbreviation) {
      setPersistedFundingPrograms([
        sessionStateFundingProgram.stateAbbreviation,
      ]);
      return;
    }

    if (currentUser?.selfElectedStateFundingProgram?.stateAbbreviation) {
      setPersistedFundingPrograms([
        currentUser.selfElectedStateFundingProgram.stateAbbreviation,
      ]);
      return;
    }
  }, [isLoggedIn, currentUser, sessionStateFundingProgram]);

  useEffect(() => {
    if (
      esaState ||
      (!isLoggedIn &&
        selfElectedStateFundingProgram.searchFiltersActive &&
        selfElectedStateFundingProgram.stateAbbreviation)
    ) {
      setPersistedFundingPrograms([
        esaState || selfElectedStateFundingProgram.stateAbbreviation,
      ]);
    }

    // if the user logged in, we deactivate the search filters from the banner
    if (isLoggedIn && selfElectedStateFundingProgram.searchFiltersActive) {
      deactivateSearchFilters();
      const newFilters = !!esaState ? [esaState] : [];
      setPersistedFundingPrograms(newFilters);
    }
  }, [
    esaState,
    selfElectedStateFundingProgram?.searchFiltersActive,
    selfElectedStateFundingProgram?.stateAbbreviation,
    sessionStateFundingProgram?.stateAbbreviation,
    isLoggedIn,
    deactivateSearchFilters,
  ]);

  /* Filters priority (from lowest to highest priority):
      - filters from query string
  */
  const filters = React.useMemo(() => {
    const filtersFromQuery = SearchFilters.fromQueryAndParams(
      location.query,
      params,
    );
    const esaSearchDate = {
      startAfter: dayjs().add(esaWaitPeriod, "days").format("YYYY-MM-DD"),
    };
    const defaultScheduleFiltersToUse = esaState ? esaSearchDate : {};
    const _filters: Record<string, any> = {
      ...defaultScheduleFiltersToUse,
      fundingPrograms: persistedFundingPrograms,
      ...filtersFromQuery,
      ...(learnerRef.current?.uid && { learnerUid: learnerRef.current.uid }),
    };

    if (_filters?.age) {
      const age = _filters.age
        ?.split(",")
        ?.map((age: $TSFixMe) => parseInt(age))
        ?.filter((age: $TSFixMe) => age < 19)
        ?.join(",");

      if (age === "") {
        delete _filters["age"];
      } else {
        _filters.age = age;
      }
    }

    if (!!_filters?.adminTags?.trim()) {
      _filters.adminTags = _filters.adminTags.split(",");
    } else {
      _filters.adminTags = undefined;
    }

    if (_filters?.theme) {
      _filters.theme = SearchFilters.convertThemeQueryToFilter(_filters.theme);
    }

    if (!_filters?.fundingPrograms?.length) {
      _filters.fundingPrograms = undefined;
    }

    const VALID_SEARCH_ORDER_VALUES = ["relevance", "new", "upcoming"];
    if (
      _filters?.order &&
      !VALID_SEARCH_ORDER_VALUES.includes(_filters.order)
    ) {
      delete _filters.order;
    }

    return _filters;
  }, [
    persistedFundingPrograms,
    location.query,
    params,
    esaState,
    esaWaitPeriod,
  ]);

  const setFilters = useCallback<SetFiltersType>(
    (filterCategory, partialFilters, options) => {
      if ("q" in partialFilters && "multiTermQuery" in filters) {
        delete filters.multiTermQuery;
      }

      if ("multiTermQuery" in partialFilters && "q" in filters) {
        delete filters.q;
      }

      const newFilters: SearchFiltersType = _.omitBy(
        SearchFilters.omitCatchalls({
          ...filters,
          ...partialFilters,
        }),
        _.isNil,
      );

      const oldFilters = _.omitBy(
        SearchFilters.omitCatchalls(filters),
        _.isNil,
      );

      if (!_.isEqual(oldFilters, newFilters)) {
        setPersistedFundingPrograms(newFilters.fundingPrograms ?? undefined);
      }

      let newFiltersWithFormattedTheme = Object.assign({}, newFilters);
      if (newFilters.theme) {
        newFiltersWithFormattedTheme.theme =
          SearchFilters.convertThemeFilterToQuery(newFilters.theme);
      }

      if (
        typeof newFiltersWithFormattedTheme?.includeInProgressFixedLengthLiveFormat ===
        "boolean"
      ) {
        // searchPath expects a string for boolean values
        // @ts-ignore
        newFiltersWithFormattedTheme.includeInProgressFixedLengthLiveFormat =
          newFiltersWithFormattedTheme.includeInProgressFixedLengthLiveFormat
            ? "true"
            : "false";
      }

      if (!options?.overrideRouting) {
        // Filter out learnerUid from URL
        const { learnerUid: _learnerUid, ...newFilters } =
          newFiltersWithFormattedTheme;
        history.push(searchPath(newFilters), {
          filterCategory,
        });
      }
    },
    [history, filters],
  );

  const value = React.useMemo(
    () => ({
      filters,
      setFilters,
    }),
    [filters, setFilters],
  );

  return (
    <SearchFiltersContext.Provider value={value}>
      {children}
    </SearchFiltersContext.Provider>
  );
}

/**
 * Type guard to check if the context is populated.
 */
function isPopulatedSearchFiltersContextValue(
  context: InternalSearchFiltersContextValue,
): context is SearchFiltersContextValue {
  return context.setFilters !== undefined && context.filters !== undefined;
}

export function useSearchFilters(): SearchFiltersContextValue {
  const context = useContext(SearchFiltersContext);
  if (!isPopulatedSearchFiltersContextValue(context)) {
    throw new Error("Called useSearchFilters without a SearchFiltersContext");
  }
  return context as SearchFiltersContextValue;
}
